import { t } from '@if/core/i18n'
import type { EFinancingCompany, EFinancingProgram } from '@if/core/types'
import { AnyError, useCan, useErrorHandler, useSnackbar } from '@if/mui'
import { access } from '@src/access'
import { useAcquireLock, useGetLock, useReleaseLock } from '@src/api/loan/loan-api'
import { LoanLockDto } from '@src/api/loan/loan-dto'
import { useSystemFlowConfig } from '@src/contexts/system-flow-context'
import { useSystemLockBanner } from '@src/providers/LoanLockProvider-hooks'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

interface LoanLockState {
  lock: LoanLockDto | null
  isLocked: boolean
  isLockedByOther: boolean
  isLockedBySystem: boolean
  isReadOnly: boolean
  isLoading: boolean
}

interface LoanLockContextValue extends LoanLockState {
  acquireLock: () => Promise<void>
  releaseLock: () => Promise<void>
}

const LoanLockContext = createContext<LoanLockContextValue | null>(null)

export const useLoanLock = () => {
  const context = useContext(LoanLockContext)
  if (!context) {
    throw new Error('useLoanLock must be used within a LoanLockProvider')
  }
  return context
}

interface LoanLockProviderProps {
  children: ReactNode
  loanId: string
  financingProgram: EFinancingProgram
  financingCompany: EFinancingCompany
}

const LoanLockProvider = ({ children, loanId, financingProgram, financingCompany }: LoanLockProviderProps) => {
  const { showSuccess, showError } = useSnackbar()
  const { report } = useErrorHandler()
  const flowConfig = useSystemFlowConfig()

  const isLockedBySystem = !useCan(access.editLoansInLms, { flowConfig, financingProgram })
  useSystemLockBanner(isLockedBySystem)

  const [lock, setLock] = useState<LoanLockDto | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const { mutateAsync: acquireLockMutation } = useAcquireLock(loanId, financingCompany)
  const { mutateAsync: releaseLockMutation } = useReleaseLock(loanId, financingCompany)
  const { data: initialLockData } = useGetLock(loanId, financingCompany, !isLockedBySystem)

  // Set initial lock state when data is loaded
  useEffect(() => {
    if (initialLockData) {
      setLock(initialLockData)
    }
  }, [initialLockData])

  const isLocked = useMemo(() => lock?.isOwnedByCurrentUser === true, [lock])

  const isLockedByOther = useMemo(() => lock?.isAcquired === true && !lock.isOwnedByCurrentUser, [lock])

  const isReadOnly = useMemo(() => {
    return isLockedByOther || isLockedBySystem
  }, [isLockedByOther, isLockedBySystem])

  const acquireLock = useCallback(async () => {
    if (isLockedBySystem) return
    setIsLoading(true)
    try {
      const result = await acquireLockMutation()

      if (result.isAcquired) {
        setLock(result)
        showSuccess(t('common.lockSuccess.acquire'))
      } else {
        // Update the lock state to reflect who currently holds it
        setLock(result)

        const expiresOn = result.expiresOn ? new Date(result.expiresOn).toLocaleString() : 'unknown'
        showError(
          t('common.lockError.failedAcquire', {
            username: result.username || 'Unknown user',
            expiresOn,
          }),
        )
      }
    } catch (error: unknown) {
      report(error as AnyError)
    } finally {
      setIsLoading(false)
    }
  }, [isLockedBySystem, acquireLockMutation, showSuccess, showError, report])

  const releaseLock = useCallback(async () => {
    if (isLockedBySystem) return
    setIsLoading(true)
    try {
      const result = await releaseLockMutation()

      if (!result.isAcquired) {
        setLock(result)
        showSuccess(t('common.lockSuccess.release'))
      } else {
        // Update the lock state to reflect who currently holds it
        setLock(result)

        const expiresOn = result.expiresOn ? new Date(result.expiresOn).toLocaleString() : 'unknown'
        showError(
          t('common.lockError.failedRelease', {
            username: result.username || 'Unknown user',
            expiresOn,
          }),
        )
      }
    } catch (error: unknown) {
      report(error as AnyError)
    } finally {
      setIsLoading(false)
    }
  }, [isLockedBySystem, releaseLockMutation, showSuccess, showError, report])

  const contextValue = useMemo<LoanLockContextValue>(
    () => ({
      lock,
      isLocked,
      isLockedByOther,
      isLockedBySystem,
      isReadOnly,
      isLoading,
      acquireLock,
      releaseLock,
    }),
    [lock, isLocked, isLockedByOther, isLockedBySystem, isReadOnly, isLoading, acquireLock, releaseLock],
  )

  return <LoanLockContext.Provider value={contextValue}>{children}</LoanLockContext.Provider>
}

export default LoanLockProvider
